import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, Button, Stack, Typography} from 'views/components';
import {Translate} from 'views/module';

import SectionHeading from '../shared/SectionHeading';
import CardGridSkeleton from '../shared/skeletons/CardGridSkeleton';
import PanelSkeleton from '../shared/skeletons/PanelSkeleton';

import ClientMonthlyClientsTable from './ClientMonthlyClientsTable';
import ClientMonthlySummaryCards from './ClientMonthlySummaryCards';
import {summaryGridColumns} from './clientMonthlyShared';

import moment from 'moment-timezone';

// Orchestrates the per-client monthly forecast: resolves which state to show (loading,
// error, missing-snapshot, empty, or content) and composes the summary cards and client
// table. All fetching, caching, and pagination live in the parent ClientMetrics scene.
const ClientMonthlyBreakdownSection = (props) => {
  const {
    isLoading,
    isLoadingMore = false,
    errorMessage = '',
    month = '',
    metricDate = '',
    clients = [],
    totals = null,
    hasSnapshot = false,
    hasMore = false,
    onLoadMore = () => {},
    onRetry = () => {},
  } = props;

  const monthLabel = month ? moment(month, 'YYYY-MM').format('MMMM YYYY') : '';
  const metricDateLabel = metricDate ? moment(metricDate, 'YYYY-MM-DD').format('MMM D, YYYY') : '';

  const hasClients = clients.length > 0;
  const showInitialSkeleton = isLoading && !hasClients;
  const showFullError = !isLoading && !hasClients && Boolean(errorMessage);
  const showMissingSnapshot = !isLoading && !hasClients && !errorMessage && !hasSnapshot;
  const showEmpty = !isLoading && !hasClients && !errorMessage && hasSnapshot;

  return (
    <Box>
      <SectionHeading expression='metrics.ClientMetrics.ClientMonthly.SectionTitle' />

      <Typography
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          lineHeight: '21px',
          color: '#1A1916',
          mb: 0.25,
        }}
      >
        <Translate expression='metrics.ClientMetrics.ClientMonthly.Title' />
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          lineHeight: '16px',
          color: '#A8A49E',
          mb: 2,
        }}
      >
        <Translate expression='metrics.ClientMetrics.ClientMonthly.Subtitle' substitute={{month: monthLabel}} />
      </Typography>

      {
        showInitialSkeleton &&
        <Stack spacing={2.5}>
          <CardGridSkeleton cardCount={6} gridTemplateColumns={summaryGridColumns} />
          <PanelSkeleton height={320} />
        </Stack>
      }

      {
        showFullError &&
        <Alert
          severity='warning'
          action={
            <Button color='inherit' size='small' onClick={onRetry}>
              <Translate expression='metrics.ClientMetrics.ClientMonthly.Retry' />
            </Button>
          }
        >
          {errorMessage}
        </Alert>
      }

      {
        showMissingSnapshot &&
        <Alert severity='info'>
          <Translate
            expression='metrics.ClientMetrics.ClientMonthly.EmptyStates.MissingSnapshot'
            substitute={{date: metricDateLabel}}
          />
        </Alert>
      }

      {
        showEmpty &&
        <Alert severity='info'>
          <Translate
            expression='metrics.ClientMetrics.ClientMonthly.EmptyStates.NoClients'
            substitute={{month: monthLabel}}
          />
        </Alert>
      }

      {
        hasClients &&
        <Stack spacing={2.5}>
          <ClientMonthlySummaryCards totals={totals} />
          <ClientMonthlyClientsTable
            clients={clients}
            totals={totals}
            month={month}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            errorMessage={errorMessage}
            onLoadMore={onLoadMore}
          />
        </Stack>
      }
    </Box>
  );
};

ClientMonthlyBreakdownSection.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  isLoadingMore: PropTypes.bool,
  errorMessage: PropTypes.string,
  month: PropTypes.string,
  metricDate: PropTypes.string,
  clients: PropTypes.array,
  totals: PropTypes.object,
  hasSnapshot: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  onRetry: PropTypes.func,
};

export default React.memo(ClientMonthlyBreakdownSection);
